// src/auth/OtpVerify.js
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import '../auth/Login.css';

const DEFAULT_RESEND_SECONDS = 60;

const OtpVerify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const API_URL = import.meta.env.VITE_API_URL;

  const initialState = (() => {
    if (location.state) return location.state;
    try {
      const raw = sessionStorage.getItem('otpFlowState');
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  })() || {};

  const {
    challengeId,
    userAddress,
    maskedEmail,
    ttlSec,
    resendCooldownSec,
    nextTo,
    returnTo,
    returnState,
    afterOtpAction,
  } = initialState;

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [successText, setSuccessText] = useState('');
  const [resendLeft, setResendLeft] = useState(resendCooldownSec ?? DEFAULT_RESEND_SECONDS);
  const [ttlLeft, setTtlLeft] = useState(ttlSec ?? 0);

  const canSubmit = useMemo(() => /^\d{6}$/.test(code) && !submitting, [code, submitting]);

  useEffect(() => {
    if (!challengeId) {
      navigate('/login', { replace: true });
    }
  }, [challengeId, navigate]);

  useEffect(() => {
    let timerId;
    if (challengeId) {
      setResendLeft(resendCooldownSec ?? DEFAULT_RESEND_SECONDS);
      if (ttlSec) setTtlLeft(ttlSec);
      timerId = setInterval(() => {
        setResendLeft((s) => (s > 0 ? s - 1 : 0));
        setTtlLeft((t) => (t > 0 ? t - 1 : 0));
      }, 1000);
    }
    return () => clearInterval(timerId);
  }, [challengeId, ttlSec, resendCooldownSec]);

  const onChangeCode = (e) => {
    const v = e.target.value.replace(/[^\d]/g, '').slice(0, 6);
    setCode(v);
    setErrorText('');
    setSuccessText('');
  };

  const proceedAfterOtp = async () => {
    sessionStorage.removeItem('otpFlowState');

    // if (afterOtpAction?.type === 'password_reset_request') {
    //   try {
    //     await axios.post(
    //       `${API_URL}/api/password-reset-requests/`,
    //       { WAMID: afterOtpAction.email || userAddress }
    //     );
    //     navigate(afterOtpAction.navigateTo || '/password-reset-sent', { replace: true });
    //     return;
    //   } catch (e) {
    //     console.error('password-reset-requests error', e?.response?.data || e?.message);
    //     setErrorText('リセットリンクの送信に失敗しました。時間をおいて再度お試しください。');
    //     return;
    //   }
    // }

    if (returnTo) {
      navigate(returnTo, { state: returnState, replace: true });
      return;
    }

    if (nextTo) {
      navigate(nextTo, { replace: true });
      return;
    }

    navigate('/', { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setErrorText('');
    setSuccessText('');

    try {
      const res = await axios.post(
        `${API_URL}/api/otp/verify`,
        { challengeId, otp: code }
      );

      if (res.data?.ok) {
        await proceedAfterOtp();
      } else {
        setErrorText('ワンタイムパスが正しくありません。もう一度入力してください。');
      }
    } catch (error) {
      console.error('status', error.response?.status);
      console.error('data', error.response?.data);
      if (error.response?.status === 429) {
        const retry = parseInt(error.response?.headers?.['retry-after'] ?? '10', 10);
        if (retry > 0) {
          setResendLeft(retry);
          setErrorText(`操作が集中しています。${retry}秒後に再送できます。`);
        } else {
          setErrorText('操作が集中しています。しばらくしてからお試しください。');
        }
      } else if (error.response?.status === 400) {
        setErrorText('ワンタイムパスが正しくありません。もう一度入力してください。');
      } else if (error.message === 'Network Error') {
        setErrorText('ネットワークエラーが発生しました。時間をおいて再度お試しください。');
      } else {
        setErrorText('確認中にエラーが発生しました。再度お試しください。');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendLeft > 0 || !challengeId) return;
    setErrorText('');
    setSuccessText('');

    try {
      await axios.post(
        `${API_URL}/api/otp/resend`,
        { challengeId }
      );
      setSuccessText('認証コードを再送しました。届くまで数十秒かかる場合があります。');
      setResendLeft(resendCooldownSec ?? DEFAULT_RESEND_SECONDS);
    } catch (e) {
      const retry = parseInt(e?.response?.headers?.['retry-after'] ?? '0', 10);
      if (e?.response?.status === 429 && retry > 0) {
        setResendLeft(retry);
        setErrorText(`操作が集中しています。${retry}秒後に再送できます。`);
        return;
      }
      setErrorText('再送に失敗しました。時間をおいて再度お試しください。');
    }
  };

  const formatMMSS = (sec) => {
    const m = Math.floor((sec || 0) / 60);
    const s = (sec || 0) % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  return (
    <div className="login-container">
      <div className="login-card" role="dialog" aria-labelledby="otp-title">
        <h2 id="otp-title" className="login-title">ワンタイムパス確認</h2>
        <p className="login-subtitle">
          {maskedEmail
            ? <>メールアドレス <strong>{maskedEmail}</strong> に送信した6桁コードを入力してください。</>
            : <>メールアドレス <strong>{userAddress}</strong> に送信した6桁コードを入力してください。</>
          }
          {ttlLeft > 0 && <>（有効期限 残り <strong>{formatMMSS(ttlLeft)}</strong>）</>}
        </p>

        {successText && <div className="success-alert" role="status">{successText}</div>}
        {errorText && <div className="error-alert" role="alert">{errorText}</div>}

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-group">
            <label htmlFor="otpCode">6桁コード</label>
            <input
              id="otpCode"
              inputMode="numeric"
              pattern="\d{6}"
              autoComplete="one-time-code"
              type="text"
              value={code}
              onChange={onChangeCode}
              placeholder="123456"
              aria-invalid={!!errorText}
              maxLength={6}
            />
            <div className="otp-hint" aria-hidden="true">半角数字6桁</div>
          </div>

          <button type="submit" className={`login-button ${submitting ? 'is-loading' : ''}`} disabled={!canSubmit}>
            {submitting ? '確認中…' : '確認する'}
          </button>
        </form>

        <div className="link-row" style={{ marginTop: 12 }}>
          コードが届きませんか？{' '}
          <button
            type="button"
            className="link"
            onClick={handleResend}
            disabled={resendLeft > 0}
            aria-disabled={resendLeft > 0}
            style={{ background: 'none', border: 'none', padding: 0, cursor: resendLeft > 0 ? 'not-allowed' : 'pointer' }}
          >
            {resendLeft > 0 ? `再送可能まで ${resendLeft}秒` : 'コードを再送'}
          </button>
        </div>

        <div className="link-row" style={{ marginTop: 6 }}>
          <Link to="/login" className="link">ログインに戻る</Link>
        </div>
      </div>
    </div>
  );
};

export default OtpVerify;